import React, { type ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import AppConstants from './app.constants';
import RouteConstants from './routes.constants';
import RegisterOfficeManagers from '../features/authentication/registration/register-office-manager.component';
import Register from '../features/authentication/registration/register.component';
import EmployeeOverview from '../features/check-in-employees/employee-overview.component';
import OfficeDashboard from '../features/dashboard/office-dashboard.component';
import UserDashboard from '../features/dashboard/user-dashboard.component';
import OfficeCreate from '../features/offices/create.component';
import OfficeEdit from '../features/offices/edit.component';
import OfficeOverview from '../features/offices/overview.component';
import FloorPlanEditor from '../features/floorplans/floorplan-editor.component';
import CompanyCreate from '../features/companies/create.component';
import CompanyEdit from '../features/companies/edit.component';
import CompanyOverview from '../features/companies/overview.component';
import StatisticsPage from '../features/statistics/statistics-page.component';
import { useKeycloak } from '../features/authentication/keycloak/KeycloakProvider';
import Dashboard from '../features/dashboard/dashboard.component';

export interface Route {
  readonly path: string;
  readonly element: ReactElement;
  readonly children?: Route[];
}

export const useRoutes = (): Route[] => {
  const keycloak = useKeycloak();

  const routesArray = [];

  if (keycloak.hasRealmRole(AppConstants.OFFICEMANAGER_ROLE)) {
    routesArray.push({
      path: RouteConstants.PATH_ROOT,
      element: <Navigate to={RouteConstants.PATH_REGISTER_EMPLOYEES} />,
    });
  } else {
    routesArray.push({
      path: RouteConstants.PATH_ROOT,
      element: <Navigate to={RouteConstants.PATH_OFFICES} />,
    });
  }

  if (keycloak.hasRealmRole(AppConstants.WHYELLOW_ROLE)) {
    routesArray.push(
      {
        path: RouteConstants.PATH_REGISTER,
        element: <Register />,
      },
      {
        path: RouteConstants.PATH_DASHBOARD,
        element: <Dashboard />,
      },
      {
        path: `${RouteConstants.PATH_DASHBOARD}${RouteConstants.PATH_DASHBOARD_OFFICES}`,
        element: <OfficeDashboard />,
      },
      {
        path: `${RouteConstants.PATH_DASHBOARD}${RouteConstants.PATH_DASHBOARD_USERS}`,
        element: <UserDashboard />,
      },
      {
        path: RouteConstants.PATH_COMPANIES,
        element: <CompanyOverview />,
      },
      {
        path: `${RouteConstants.PATH_COMPANIES}${RouteConstants.PATH_CREATE}`,
        element: <CompanyCreate />,
      },
      {
        path: `${RouteConstants.PATH_COMPANIES}${RouteConstants.PATH_EDIT}`,
        element: <CompanyEdit />,
      },
    );
  }

  if (keycloak.hasRealmRole(AppConstants.WHYELLOW_ROLE) || keycloak.hasRealmRole(AppConstants.ADMIN_ROLE)) {
    routesArray.push(
      {
        path: RouteConstants.PATH_REGISTER_OFFICEMANAGERS,
        element: <RegisterOfficeManagers />,
      },
      {
        path: RouteConstants.PATH_OFFICES,
        element: <OfficeOverview />,
      },
      {
        path: `${RouteConstants.PATH_OFFICES}${RouteConstants.PATH_CREATE}`,
        element: <OfficeCreate />,
      },
      {
        path: `${RouteConstants.PATH_OFFICES}${RouteConstants.PATH_EDIT}`,
        element: <OfficeEdit />,
      },
      {
        // Editing a floorplan needs the same rights as uploading one: the whyellow or
        // administrator realm role, plus administrator rights on the office itself, which
        // the API checks per request.
        path: `${RouteConstants.PATH_OFFICES}${RouteConstants.PATH_EDIT}${RouteConstants.PATH_FLOORPLAN_EDITOR}`,
        element: <FloorPlanEditor />,
      },
    );
  }

  if (
    keycloak.hasRealmRole(AppConstants.WHYELLOW_ROLE) ||
    keycloak.hasRealmRole(AppConstants.OFFICEMANAGER_ROLE) ||
    keycloak.hasRealmRole(AppConstants.ADMIN_ROLE)
  ) {
    routesArray.push({
      path: RouteConstants.PATH_STATISTICS,
      element: <StatisticsPage />,
    });
  }

  if (keycloak.hasRealmRole(AppConstants.WHYELLOW_ROLE) || keycloak.hasRealmRole(AppConstants.OFFICEMANAGER_ROLE)) {
    routesArray.push({
      path: RouteConstants.PATH_REGISTER_EMPLOYEES,
      element: <EmployeeOverview />,
    });
  }

  return routesArray;
};
